/**
 * ContentMetadataForm
 *
 * Name + Notes fields for a content document variant, with a Save button.
 */
import Save from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

interface ContentMetadataFormProps {
  name: string;
  notes: string;
  isSaving: boolean;
  isDirty: boolean;
  onNameChange: (value: string) => void;
  onNotesChange: (value: string) => void;
  onSave: () => void;
  extraAction?: React.ReactNode;
}

const INPUT_SX = {
  borderRadius: 1.5,
  bgcolor: 'action.hover',
};
const LABEL_SX = { fontSize: '0.8rem', opacity: 0.7 };

export function ContentMetadataForm({
  name,
  notes,
  isSaving,
  isDirty,
  onNameChange,
  onNotesChange,
  onSave,
  extraAction,
}: ContentMetadataFormProps) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: 2,
        pt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <TextField
        label={t('common.name')}
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        size="small"
        fullWidth
        variant="filled"
        InputProps={{ disableUnderline: true, sx: INPUT_SX }}
        InputLabelProps={{ sx: LABEL_SX }}
      />
      <TextField
        label={t('common.notes')}
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        size="small"
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        variant="filled"
        InputProps={{ disableUnderline: true, sx: INPUT_SX }}
        InputLabelProps={{ sx: LABEL_SX }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {extraAction}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Save />}
          onClick={onSave}
          disabled={isSaving || !isDirty}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: theme => `0 4px 14px 0 ${theme.palette.primary.main}40`,
            '&:hover': { boxShadow: theme => `0 6px 20px 0 ${theme.palette.primary.main}60` },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' },
          }}
        >
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
      </Box>
    </Box>
  );
}
